/**
 * What you see once signed in — design/PROTOTYPE-SPEC.md §2.
 *
 * Sidebar, then a scrolling main area with a per-page header: date eyebrow,
 * large light title, and the green "Add stock" button that is reachable from
 * every screen. Booking in a delivery is something a clinic does while standing
 * at an open box, so it must never be more than one click away.
 *
 * Dashboard figures load here rather than only inside the dashboard, because the
 * sidebar shows stock-on-hand and a reorder badge on every page.
 *
 * Refresh runs in both directions and they are not the same thing. `onChanged`
 * goes UP: a page tells the shell it wrote something, so the sidebar figures are
 * refetched. `refreshToken` goes DOWN: it is bumped by the same `reload()`, and
 * every page folds it into its own load effect so a write from the shell — the
 * "Add stock" button, reachable from every screen — refetches the page you are
 * looking at. Only wiring the upward half leaves the header booking a delivery
 * into a table that still shows the old quantity, which reads as a failed save
 * and invites booking the same delivery in twice.
 */
import {
  SUPPORT_EMAIL,
  type Plan as CorePlan,
  type Role as CoreRole,
} from '@inventory-tracker/core';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { getDashboard, listProducts, logout, type DashboardResponse, type Me } from '../lib/api.js';
import { c, display, primaryButton, sans } from '../theme.js';
import { AccountPage } from './AccountPage.js';
import { AuditPage } from './AuditPage.js';
import { SetupWizard } from './SetupWizard.js';
import { LearnPage } from './LearnPage.js';
import { TourOverlay } from './tour/TourOverlay.js';
import { stepsForPlan, type TourStep } from './tour/steps.js';
import { ActivityPage } from './ActivityPage.js';
import { AddStockModal } from './AddStockModal.js';
import { HeaderSearch } from './HeaderSearch.js';
import { ProductDrawer } from './ProductDrawer.js';
import { DashboardPage } from './DashboardPage.js';
import { ExpiryPage } from './ExpiryPage.js';
import { InventoryPage } from './InventoryPage.js';
import { ManagePage } from './ManagePage.js';
import { QuickSwitch } from './QuickSwitch.js';
import { AnalyticsPage } from './AnalyticsPage.js';
import { TargetsPage } from './TargetsPage.js';
import { PromotionsPage } from './PromotionsPage.js';
import { Sidebar, type Page } from './Sidebar.js';
import { StockCountPage } from './StockCountPage.js';
import { TeamPage } from './TeamPage.js';

const PAGE_TITLE: Record<Page, string> = {
  dashboard: 'Dashboard',
  inventory: 'Inventory',
  count: 'Stock count',
  expiry: 'Expiry',
  activity: 'Activity',
  audit: 'Audit log',
  learn: 'Learn',
  promotions: 'Promotions',
  analytics: 'Analytics',
  targets: 'Targets',
  team: 'Team',
  manage: 'Import & export',
  account: 'Account',
};

const PAGE_BLURB: Partial<Record<Page, string>> = {
  inventory: 'Everything you track, with batch status and value.',
  expiry: 'Batches by urgency, and what you have written off.',
  activity: 'Every movement, with who and why. Search a lot number to trace it.',
  audit: 'Who changed what, and when. Append-only: nothing here can be edited or removed.',
  learn: 'Short guides, common questions, and a tour you can replay any time.',
  promotions: 'Time-boxed offers on retail lines, and what is worth promoting.',
  analytics:
    'Where the stock money went, how long each product will last, and what is going to waste.',
  targets: 'What this month has to bring in, and how close you are.',
  count:
    'Walk the shelf and enter what you can physically see. Differences become audited adjustments.',
  manage: 'Bring your existing stock list in from a spreadsheet, or take a copy out.',
};

const DAYS = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
const MONTHS = [
  'January',
  'February',
  'March',
  'April',
  'May',
  'June',
  'July',
  'August',
  'September',
  'October',
  'November',
  'December',
];

/**
 * "Saturday 10 October 2026" from the API's `today`, which is already the clinic's date
 * in Europe/London. Built in UTC from its three numbers so the browser's own
 * time zone cannot move it to the day before.
 */
function eyebrowDate(iso: string): string {
  const [y, m, d] = iso.slice(0, 10).split('-').map(Number);
  if (!y || !m || !d) return '';
  return `${DAYS[new Date(Date.UTC(y, m - 1, d)).getUTCDay()]} ${d} ${MONTHS[m - 1]} ${y}`;
}

interface Props {
  me: Me;
  onSignedOut: () => void;
  /**
   * Refetches /auth/me. Distinct from `reload()`, which refreshes the sidebar
   * figures: renaming the clinic changes `me` itself, and the sidebar shows the
   * clinic name — so a rename that only bumped `refreshKey` would leave the old
   * name on screen until the next full page load.
   */
  onAccountChanged: () => void;
}

export function AppShell({ me, onSignedOut, onAccountChanged }: Props) {
  const [page, setPage] = useState<Page>('dashboard');
  /** The slide-in menu on a phone. Ignored on wider screens. */
  const [menuOpen, setMenuOpen] = useState(false);
  // Escape closes the phone menu, as it closes every other panel.
  useEffect(() => {
    if (!menuOpen) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setMenuOpen(false);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [menuOpen]);
  const [signingOut, setSigningOut] = useState(false);
  const [switching, setSwitching] = useState(false);
  const [addingStock, setAddingStock] = useState(false);
  /** A product picked from the header search; its drawer opens over any page. */
  const [searchedId, setSearchedId] = useState<string | null>(null);
  const [exporting, setExporting] = useState(false);
  const [exportError, setExportError] = useState<string | null>(null);

  /**
   * The header's Export: the whole product list as a spreadsheet, from any
   * page. Same file as Manage stock's "Export to Excel" — it calls the same
   * function, so the two cannot produce different columns. The spreadsheet
   * library is a megabyte and is only downloaded on the first click.
   */
  async function exportAll() {
    if (exporting) return;
    setExporting(true);
    setExportError(null);
    try {
      const [{ products }, { exportProducts }] = await Promise.all([
        listProducts(),
        import('./spreadsheet.js'),
      ]);
      await exportProducts(products, me.account.clinicName);
    } catch {
      setExportError('Could not build the export file. Try again.');
    } finally {
      setExporting(false);
    }
  }
  const [dash, setDash] = useState<DashboardResponse | null>(null);
  /** Bumped on every write so each page refetches — see the note at the top. */
  const [refreshKey, setRefreshKey] = useState(0);

  /**
   * Shown over the app, not instead of it: the dashboard renders behind the
   * dialog so a new clinic can see what they are setting up, and skipping does
   * not need a page transition.
   */
  const [wizardDone, setWizardDone] = useState(false);
  const showWizard = me.account.onboardedAt === null && !wizardDone;

  /**
   * Null when no tour is running. Holds the STEPS as well as the position,
   * because a guide's "Show me how" runs through the same overlay with a
   * different list — one measuring implementation, not two.
   *
   * Steps are captured at start rather than recomputed: re-deriving them
   * mid-tour would renumber "3 of 8" under the reader if the plan changed in
   * another tab.
   */
  const [tour, setTour] = useState<{ label: string; steps: TourStep[]; index: number } | null>(
    null,
  );

  const quickTour = useMemo(
    () => stepsForPlan(me.subscription.plan as CorePlan),
    [me.subscription.plan],
  );

  const startTour = useCallback(
    () => setTour({ label: 'Quick tour', steps: quickTour, index: 0 }),
    [quickTour],
  );
  const startGuide = useCallback(
    (label: string, steps: TourStep[]) => setTour({ label, steps, index: 0 }),
    [],
  );

  const reload = useCallback(() => {
    setRefreshKey((n) => n + 1);
    void getDashboard()
      .then(setDash)
      // The sidebar figures are decoration; losing them must not take the page
      // down with them.
      .catch(() => undefined);
  }, []);

  useEffect(() => {
    reload();
  }, [reload]);

  async function signOut() {
    setSigningOut(true);
    await logout();
    onSignedOut();
  }

  return (
    <>
      {tour && (
        <TourOverlay
          label={tour.label}
          steps={tour.steps}
          index={tour.index}
          onIndex={(i) => setTour((t) => (t ? { ...t, index: i } : t))}
          onNavigate={setPage}
          onClose={() => {
            setTour(null);
            // Back to Learn, which is where it was started from and where it can
            // be started again. The quick tour from setup has no Learn to return
            // to yet on a first run, but landing there is the better default.
            setPage('learn');
          }}
        />
      )}
      {showWizard && (
        <SetupWizard
          me={me}
          onStartTour={startTour}
          onFinished={() => {
            // Close locally AND refetch: the flag is set server-side, but waiting
            // on the round trip would leave the dialog up for a beat after the
            // final click, which reads as an unresponsive button.
            setWizardDone(true);
            onAccountChanged();
            reload();
          }}
        />
      )}
      {/* Phone layout (27 October 2026). Below 860px the sidebar would take
          half the screen, so it becomes a menu that slides in over the page,
          opened from this top bar. Above 860px nothing here shows. */}
      <style>{PHONE_CSS}</style>
      <div className="gs-topbar">
        <button
          type="button"
          aria-label="Open menu"
          aria-expanded={menuOpen}
          onClick={() => setMenuOpen(true)}
          style={{
            width: 40,
            height: 40,
            border: `1px solid ${c.border}`,
            borderRadius: 8,
            background: c.card,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            cursor: 'pointer',
            color: c.ink,
            flexShrink: 0,
          }}
        >
          <svg
            width="18"
            height="18"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="1.8"
            aria-hidden="true"
          >
            <path d="M4 7h16M4 12h16M4 17h16" />
          </svg>
        </button>
        <div style={{ minWidth: 0, flex: 1 }}>
          <div style={{ fontSize: 15, fontWeight: 500 }}>the good stock.</div>
          <div
            style={{
              fontSize: 12.5,
              color: c.muted,
              whiteSpace: 'nowrap',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
            }}
          >
            {me.account.clinicName}
          </div>
        </div>
      </div>
      {menuOpen && (
        <div className="gs-scrim" aria-hidden="true" onClick={() => setMenuOpen(false)} />
      )}
      <div
        className="gs-shell"
        style={{
          minHeight: '100vh',
          background: c.appBg,
          color: c.ink,
          fontFamily: sans,
          display: 'flex',
          alignItems: 'flex-start',
        }}
      >
        <Sidebar
          className={menuOpen ? 'gs-side gs-side-open' : 'gs-side'}
          me={me}
          page={page}
          onNavigate={(p) => {
            setPage(p);
            setMenuOpen(false);
            window.scrollTo(0, 0);
          }}
          onSwitchUser={() => setSwitching(true)}
          onSignOut={() => void signOut()}
          signingOut={signingOut}
          stockValuePence={dash?.kpis.stockValuePence ?? null}
          totalUnits={dash?.kpis.totalUnits ?? null}
          reorderCount={dash?.kpis.reorderCount ?? null}
          expiringCount={dash?.kpis.expiringCount ?? null}
        />

        {/* Centred in the space beside the sidebar. Without the auto margin a
            wide monitor shows the page hard against the sidebar with an empty
            band down the right. */}
        <main
          className="gs-main"
          style={{
            flex: 1,
            minWidth: 0,
            padding: '26px 34px 64px',
            maxWidth: 1280,
            margin: '0 auto',
          }}
        >
          {me.subscription.readOnly && (
            <ReadOnlyBanner
              me={me}
              onChoosePlan={() => {
                setPage('account');
                // The plans are below the account details; take them straight there.
                window.setTimeout(() => {
                  document
                    .querySelector('[data-tour="plancard"]')
                    ?.scrollIntoView({ behavior: 'smooth', block: 'start' });
                }, 60);
              }}
            />
          )}

          <header
            style={{
              display: 'flex',
              alignItems: 'flex-end',
              justifyContent: 'space-between',
              gap: 20,
              marginBottom: 22,
              flexWrap: 'wrap',
            }}
          >
            {/* Takes the spare width and wraps inside it, so a long clinic name
                breaks onto a second line rather than pushing Add stock off its
                corner. */}
            <div style={{ flex: '1 1 360px', minWidth: 0 }}>
              <div style={{ fontSize: 13, color: c.faint, minHeight: 17 }}>
                {dash ? eyebrowDate(dash.today) : ''}
              </div>
              <h1
                style={{
                  ...display,
                  fontSize: 28,
                  fontWeight: 500,
                  letterSpacing: '-0.02em',
                  margin: '4px 0 0',
                }}
              >
                {page === 'dashboard' ? me.account.clinicName : PAGE_TITLE[page]}
              </h1>
              {PAGE_BLURB[page] && (
                <p style={{ fontSize: 15, color: c.muted, margin: '8px 0 0', maxWidth: 640 }}>
                  {PAGE_BLURB[page]}
                </p>
              )}
            </div>

            {/* design/PROTOTYPE-SPEC.md §2: search, Export, Add stock — on every page. */}
            <div style={{ display: 'flex', gap: 6, alignItems: 'center', flexWrap: 'wrap' }}>
              <HeaderSearch onPick={setSearchedId} refreshToken={refreshKey} />
              <button
                type="button"
                onClick={() => void exportAll()}
                disabled={exporting}
                style={{
                  background: c.card,
                  border: `1px solid ${c.border}`,
                  color: c.ink,
                  fontSize: 14,
                  fontWeight: 500,
                  fontFamily: sans,
                  height: 36,
                  padding: '0 14px',
                  borderRadius: 8,
                  cursor: exporting ? 'default' : 'pointer',
                  opacity: exporting ? 0.6 : 1,
                  flexShrink: 0,
                }}
              >
                {exporting ? 'Exporting…' : 'Export'}
              </button>
              <button
                type="button"
                data-tour="addstock"
                onClick={() => setAddingStock(true)}
                style={{
                  background: c.green,
                  border: 'none',
                  color: c.white,
                  fontSize: 14,
                  fontWeight: 500,
                  fontFamily: sans,
                  height: 36,
                  padding: '0 16px',
                  borderRadius: 8,
                  cursor: 'pointer',
                  flexShrink: 0,
                }}
              >
                Add stock
              </button>
            </div>
          </header>

          {exportError && (
            <div
              role="alert"
              style={{
                background: c.dangerBg,
                border: `1px solid ${c.dangerLine}`,
                borderRadius: 18,
                padding: '12px 16px',
                marginBottom: 16,
                fontSize: 15,
                color: '#5A2511',
              }}
            >
              {exportError}
            </div>
          )}

          {page === 'dashboard' && <DashboardPage key={refreshKey} />}
          {page === 'inventory' && <InventoryPage onChanged={reload} refreshToken={refreshKey} />}
          {page === 'count' && <StockCountPage onApplied={reload} refreshToken={refreshKey} />}
          {page === 'expiry' && <ExpiryPage onChanged={reload} refreshToken={refreshKey} />}
          {page === 'activity' && <ActivityPage onChanged={reload} refreshToken={refreshKey} />}
          {page === 'analytics' && <AnalyticsPage me={me} refreshToken={refreshKey} />}
          {page === 'targets' && (
            <TargetsPage me={me} onChanged={reload} refreshToken={refreshKey} />
          )}
          {page === 'promotions' && (
            <PromotionsPage
              me={me}
              role={me.user.role as CoreRole}
              onChanged={reload}
              refreshToken={refreshKey}
            />
          )}
          {page === 'team' && <TeamPage me={me} />}
          {page === 'manage' && (
            <ManagePage
              clinicName={me.account.clinicName}
              role={me.user.role as CoreRole}
              onChanged={reload}
              refreshToken={refreshKey}
            />
          )}
          {page === 'account' && <AccountPage me={me} onChanged={onAccountChanged} />}
          {page === 'audit' && <AuditPage refreshToken={refreshKey} />}
          {page === 'learn' && (
            <LearnPage me={me} onStartTour={startTour} onStartGuide={startGuide} />
          )}
        </main>

        {switching && (
          <QuickSwitch
            currentName={me.user.name}
            onClose={() => setSwitching(false)}
            onSwitched={() => {
              setSwitching(false);
              // The cookie now belongs to someone else, so re-read who that is
              // rather than trusting the props we were rendered with.
              onSignedOut();
            }}
          />
        )}

        {addingStock && <AddStockModal onClose={() => setAddingStock(false)} onAdded={reload} />}

        {/* Opened from the header search, over whichever page is showing. Logging
          use or a sale here calls `reload`, which refreshes that page too. */}
        {searchedId && (
          <ProductDrawer
            productId={searchedId}
            onClose={() => setSearchedId(null)}
            onChanged={reload}
          />
        )}
      </div>
    </>
  );
}

/**
 * Shown on every page once the trial or subscription has ended. Says what
 * happened, that nothing is lost, and the one thing to do. Only the owner can
 * pay, so everyone else is told who to ask instead of shown a button that
 * would refuse them.
 */
function ReadOnlyBanner({ me, onChoosePlan }: { me: Me; onChoosePlan: () => void }) {
  const isOwner = me.user.role === 'owner';
  const what =
    me.subscription.readOnlyReason === 'trial_ended'
      ? 'Your free trial has ended.'
      : 'Your subscription has ended.';
  // A slim notice, not a slab (27 October 2026): one line where it fits,
  // the action as a compact button, the colour in a pale fill and thin line.
  return (
    <div
      role="status"
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 12,
        flexWrap: 'wrap',
        background: '#FFFBF2',
        border: `1px solid ${c.amberLine}`,
        borderRadius: 10,
        padding: '10px 14px',
        marginBottom: 18,
      }}
    >
      <svg
        width="16"
        height="16"
        viewBox="0 0 24 24"
        fill="none"
        stroke="#9A6A0A"
        strokeWidth="1.8"
        aria-hidden="true"
        style={{ flexShrink: 0 }}
      >
        <circle cx="12" cy="12" r="9" />
        <path d="M12 7v5l3 2" />
      </svg>
      <div style={{ minWidth: 0, flex: '1 1 380px', fontSize: 13.5, lineHeight: 1.5 }}>
        <span style={{ fontWeight: 600, color: c.ink }}>{what}</span>{' '}
        <span style={{ color: '#6B5A35' }}>
          Your records are safe and you can still see and export everything.{' '}
          {isOwner
            ? 'Choose a plan to keep adding and logging stock.'
            : 'Ask the clinic owner to choose a plan so you can keep logging.'}{' '}
          Questions?{' '}
          <a href={`mailto:${SUPPORT_EMAIL}`} style={{ color: c.green }}>
            {SUPPORT_EMAIL}
          </a>
        </span>
      </div>
      {isOwner && (
        <button
          type="button"
          onClick={onChoosePlan}
          style={{
            ...primaryButton,
            width: 'auto',
            height: 32,
            padding: '0 14px',
            fontSize: 13,
            borderRadius: 8,
            flexShrink: 0,
          }}
        >
          Choose a plan
        </button>
      )}
    </div>
  );
}

/**
 * Everything that changes below 860px wide. Kept as one stylesheet rather than
 * spread through the pages: inline styles cannot hold a media query, and one
 * place is easier to keep right than twenty.
 *
 * - The sidebar is fixed off the left edge and slides in when opened.
 * - The page gets the full width and phone-sized padding.
 * - Side-by-side grids inside the page stack into one column, except the
 *   figure strip, which keeps two (DashboardPage sets that). Label-and-value
 *   rows (`minmax(0, 1fr) auto`) are left alone.
 * - Tables scroll sideways inside their own box instead of squeezing.
 * - The header search fills the row.
 */
const PHONE_CSS = `
.gs-topbar{display:none}
.gs-scrim{display:none}
@media (max-width:860px){
  .gs-topbar{display:flex;align-items:center;gap:12px;position:sticky;top:0;z-index:40;
    padding:10px 16px;background:${c.appBg};border-bottom:1px solid ${c.border};font-family:${sans};color:${c.ink}}
  .gs-shell{display:block !important}
  .gs-side{position:fixed !important;left:0;top:0;bottom:0;z-index:60;height:100% !important;
    transform:translateX(-105%);transition:transform 220ms cubic-bezier(0.22,1,0.36,1);
    box-shadow:0 10px 40px rgba(0,0,0,0.18);overflow-y:auto}
  .gs-side.gs-side-open{transform:none}
  .gs-scrim{display:block;position:fixed;inset:0;z-index:50;background:rgba(20,24,20,0.32)}
  .gs-main{padding:16px 16px 56px !important;max-width:none !important}
  .gs-main [style*="grid-template-columns: repeat("]:not(.gs-kpis),
  .gs-main [style*="grid-template-columns: 1fr 1"]{grid-template-columns:minmax(0,1fr) !important}
  .gs-main table{display:block;overflow-x:auto;-webkit-overflow-scrolling:touch}
  .gs-main th,.gs-main td{white-space:nowrap}
  .gs-main th:first-child,.gs-main td:first-child{white-space:normal;min-width:180px}
  .gs-main .gs-search{width:100% !important;flex:1 1 100%}
}
@media (prefers-reduced-motion:reduce){.gs-side{transition:none}}
`;
